import { inject, Injectable } from '@angular/core';
import { omit, pick } from 'lodash-es';

import {
  CanvasElementOverrides,
  CanvasElementType,
  PropsWithOverrides,
} from '@blaze-runtime/engine-domain';
import { isEmptyish } from '@blaze-shared/shared-util';

import { forEachOverride } from '../../canvas-elements/element-model/props-overrides';
import { ElementDefinitionsService } from '../element-definitions.service';

const filterObjectByPaths = <T extends object>(
  obj: T,
  includedPaths: string[] = [],
  excludedPaths: string[] = [],
): Partial<T> => {
  const included = pick<T>(obj, includedPaths);
  return omit<Partial<T>>(included, excludedPaths);
};

@Injectable({ providedIn: 'root' })
export class StyleHelperService {
  private readonly elementDefinitionsService = inject(ElementDefinitionsService);

  extractStyleProps<T>(props: T, elementType: CanvasElementType, throwIfEmpty = true): Partial<T> {
    const definition = this.elementDefinitionsService.getDefinition(elementType);

    if (!definition) {
      throw new Error(`Could not find definition for element of type: ${elementType}`);
    }

    if (!props || typeof props !== 'object') {
      throw new Error(`Could not find props for element of type: ${elementType}`);
    }

    const filtered = filterObjectByPaths(
      props,
      definition.styleIncludePaths,
      definition.styleExcludePaths,
    );

    if (throwIfEmpty && isEmptyish(filtered)) {
      throw new Error('No props were found to save as style!');
    }

    return filtered;
  }

  extractStylePropsWithOverrides(
    props: unknown,
    overrides: CanvasElementOverrides | undefined,
    elementType: CanvasElementType,
    throwIfEmpty = true,
  ): PropsWithOverrides {
    let updatesCount = 0;
    const result: PropsWithOverrides = {
      props: this.extractStyleProps(props, elementType, false),
    };

    if (!isEmptyish(result.props)) {
      updatesCount++;
    }

    forEachOverride(overrides, (override, target) => {
      if (!override.props) {
        return;
      }

      const extracted = this.extractStyleProps(override.props, elementType, false);
      if (!isEmptyish(extracted)) {
        updatesCount++;
      }
      result.overrides ??= {};
      result.overrides[target] = { props: extracted };
    });

    if (throwIfEmpty && updatesCount === 0) {
      throw new Error('No props were found to save as style!');
    }

    return result;
  }
}
